import { useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Pencil, Settings } from "lucide-react";
import { toast } from "sonner";
import { ActionBar } from "@/components/layout/action-bar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { trpc, toastTrpcError } from "@/lib/trpc";
import type { Project } from "@/components/project-context";

export function ProjectIdentityTitle({ project }: { project: Project }) {
  const urlLabel = project.productUrl?.replace(/^https?:\/\//, "");
  const initial = project.name ? project.name[0]?.toUpperCase() : "P";

  return (
    <span className="inline-flex items-center gap-3">
      {/* Project Avatar with Origami Planar Fold */}
      <span
        className="relative flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border/80 bg-muted/30 text-xs font-bold text-foreground shadow-xs select-none"
        aria-hidden="true"
      >
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox="0 0 32 32"
          fill="none"
          preserveAspectRatio="none"
        >
          {/* Origami Planar Facets */}
          <polygon
            points="0,0 32,0 18,32 0,32"
            fill="currentColor"
            opacity="0.04"
          />
          <polygon
            points="18,32 32,0 32,32"
            fill="currentColor"
            opacity="0.12"
          />
          <line
            x1="32"
            y1="0"
            x2="18"
            y2="32"
            stroke="var(--color-primary)"
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity="0.8"
          />
        </svg>
        <span className="relative z-10">{initial}</span>
      </span>

      <span className="inline-flex flex-wrap items-baseline gap-x-3.5 gap-y-1">
        <span>{project.name}</span>
        {project.productUrl && urlLabel && (
          <a
            href={project.productUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 font-sans text-xs font-normal text-muted-foreground hover:text-primary hover:underline transition-colors"
          >
            {urlLabel}
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        )}
      </span>
    </span>
  );
}

export function ProjectIdentityActions({
  project,
  onEdit,
}: {
  project: Project;
  onEdit: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Button variant="ghost" size="sm" onClick={onEdit}>
        <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
        Edit
      </Button>
      <Button asChild variant="ghost" size="sm">
        <Link to={`/projects/${project.id}/settings`}>
          <Settings className="mr-2 h-4 w-4" aria-hidden="true" />
          Settings
        </Link>
      </Button>
    </div>
  );
}

export function ProjectIdentityEditCard({
  project,
  onDone,
}: {
  project: Project;
  onDone: () => void;
}) {
  const utils = trpc.useUtils();
  const [name, setName] = useState(project.name);
  const [productUrl, setProductUrl] = useState(project.productUrl ?? "");
  const [description, setDescription] = useState(project.description ?? "");

  const updateMutation = trpc.project.update.useMutation({
    onSuccess: () => {
      toast.success("Project updated");
      utils.project.getById.invalidate({ id: project.id });
      utils.project.list.invalidate();
      onDone();
    },
    onError: (err) => toastTrpcError(err, "Failed to update project"),
  });

  const hasChanges =
    name.trim() !== project.name ||
    productUrl.trim() !== (project.productUrl ?? "") ||
    description.trim() !== (project.description ?? "");

  const canSave = name.trim().length > 0 && hasChanges;

  function handleSave() {
    if (!canSave) return;
    updateMutation.mutate({
      id: project.id,
      name: name.trim() || undefined,
      productUrl: productUrl.trim() || null,
      description: description.trim() || null,
    });
  }

  return (
    <Card>
      <CardContent className="space-y-5 p-8">
        <div className="space-y-2">
          <Label htmlFor="identity-name">Name</Label>
          <Input
            id="identity-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="identity-url">URL</Label>
          <Input
            id="identity-url"
            value={productUrl}
            onChange={(e) => setProductUrl(e.target.value)}
            placeholder="https://example.com"
            inputMode="url"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="identity-description">Description</Label>
          <Textarea
            id="identity-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What does this project evaluate?"
            rows={5}
            aria-describedby="identity-description-help"
          />
          <p
            id="identity-description-help"
            className="text-xs text-muted-foreground"
          >
            Used as context for personas, scenarios, and findings.
          </p>
        </div>

        <ActionBar
          className="pt-2"
          primary={
            <Button
              onClick={handleSave}
              disabled={!canSave || updateMutation.isPending}
            >
              {updateMutation.isPending ? "Saving…" : "Save"}
            </Button>
          }
          tertiary={[
            <Button
              key="cancel"
              onClick={onDone}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>,
          ]}
        />
      </CardContent>
    </Card>
  );
}
